import classNames from "classnames";
import React from "react";
import Blok from "../../../interfaces/Storyblok/Blok.interface";
import { isEditMode } from "../../../utils/isEditMode.util";
import CTACard, { CTACardProps } from "./CTACard.blok";
import BlokWithBorderIcons from "../../../interfaces/Storyblok/BlokWithBorderIcons.interface";
import BorderIconsFactory from "../../utils/BorderIconsFactory/BorderIconsFactory.util";
import { BorderIconType } from "../../utils/BorderIconsFactory/BorderIcon.util";

interface CTACardsListProps extends BlokWithBorderIcons {
  title?: string;
  useBrandBackground?: boolean;
  cards: CTACardProps[];
}

const CTACardsList: React.FC<CTACardsListProps> = ({
  title,
  useBrandBackground = false,
  cards,
  border_icon_top_left,
  border_icon_top_right,
  border_icon_bottom_left,
  border_icon_bottom_right,
}) => {
  const getEditModeCTACard = (index: number): CTACardProps => {
    const EMPTY_CTA_CARD: CTACardProps = {
      _uid: `empty_cta_card_${index}`,
      component: "cta_card",
      _editable: "",
      image: undefined,
      heading: undefined,
      body: undefined,
      cta: [],
    };
    return cards[index] ?? EMPTY_CTA_CARD;
  };

  const wrapperClassNames = classNames({ "bg-Primary-700": useBrandBackground });

  const titleClassNames = classNames("font-extrabold text-2xl md:text-3xl xl:text-4xl mb-8 lg:mb-10", {
    "text-White": useBrandBackground,
    "text-Primary-900": !useBrandBackground,
  });

  const renderTitle = () =>
    !!title && <h2 className={titleClassNames}>{title}</h2>;

  if (isEditMode()) {
    return (
      <div className={wrapperClassNames}>
        <BorderIconsFactory
          type={BorderIconType.Blok}
          border_icon_top_left={border_icon_top_left}
          border_icon_top_right={border_icon_top_right}
          border_icon_bottom_left={border_icon_bottom_left}
          border_icon_bottom_right={border_icon_bottom_right}
        >
          <div className="container mx-auto pt-16 pb-10 lg:py-16 xl:py-20">
            {renderTitle()}
            <ul className="grid grid-cols-2 gap-y-6 md:grid-cols-12 md:gap-x-6 md:gap-y-10">
              {Array(cards.length < 3 ? 3 : cards.length)
                .fill(1)
                .map((_, index) => (
                  <CTACard
                    key={`loading-cta-card--${index + 1}`}
                    {...getEditModeCTACard(index)}
                    useBrandBackground={useBrandBackground}
                  />
                ))}
            </ul>
          </div>
        </BorderIconsFactory>
      </div>
    );
  }

  return (
    <>
      {!!cards.length && (
        <div className={wrapperClassNames}>
          <BorderIconsFactory
            type={BorderIconType.Blok}
            border_icon_top_left={border_icon_top_left}
            border_icon_top_right={border_icon_top_right}
            border_icon_bottom_left={border_icon_bottom_left}
            border_icon_bottom_right={border_icon_bottom_right}
          >
            <div
              className={classNames("container mx-auto", {
                "py-12 md:py-16 lg:py-20": useBrandBackground,
                "lg:py-12": !useBrandBackground,
              })}
            >
              {renderTitle()}
              <ul className="grid grid-cols-2 gap-y-6 md:grid-cols-12 md:gap-x-6 md:gap-y-10">
                {cards.map((card: CTACardProps, index: number) => (
                  <CTACard key={`cta-card--${index + 1}`} {...card} useBrandBackground={useBrandBackground} />
                ))}
              </ul>
            </div>
          </BorderIconsFactory>
        </div>
      )}
    </>
  );
};

export default CTACardsList;
